import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@eventr/capacitor-keyboard";
import { type NavigateFunction } from "react-router-dom";
import { surfacedExistingView } from "feat/nativeNavigation/NativeRootController";
import { whenNativeNavSettled } from "feat/nativeNavigation/root-state";
import { prepareSheetNavigation } from "feat/nativeNavigation/sheetNavigation";
import { isPathTabDisabled } from "feat/layout/tab-bar/tabDisabled.store";
import { preloadRoute } from "routing/tree";
import { blocksSelfNavigation } from "feat/viewedUser/viewedUserNavigation";
import { isChangingLanguage } from "./navigationGuards";
import { queueViewTransition } from "./viewTransitionQueue";
import type { PaneSide } from "feat/layout/panes/pane-context";

/** The view transition type that keeps the leading pane still while the trailing pane slides. */
export const TRAILING_PANE_TRANSITION = "trailing-pane";

/** The slice of navigation state the native layer reads; the rest is the caller's own. */
export interface NativeNavigationState {
  /** An id dismisses that component's root; `true` dismisses whatever root is on top. */
  dismiss?: boolean | string;
  /** The stack to push onto, in place of the stack of the screen pushing. */
  target?: string;
  /** Replace that stack's screens rather than push on top of them. */
  root?: boolean;
  /** False to show the pushed screen in place rather than slide it in. */
  animated?: boolean;
}

/** React-router's `state` is untyped. Naming the one key we own restores checking on it. */
export interface PushPathState {
  nativeNavigation?: NativeNavigationState;
  [key: string]: unknown;
}

export interface PushPathOptions {
  animation?: "auto" | "none" | "right" | "left";
  state?: PushPathState;
  /** The two-pane pane the push comes from; none counts as the leading pane. */
  paneSide?: PaneSide;
  /** Set when the push changes only the trailing pane, so only that pane slides. */
  trailingPaneOnly?: boolean;
  /** Under iOS native navigation, the stack of the screen pushing. */
  nativeStackId?: string;
}

/** The single push implementation behind both `window.pushPath` and `useGlobalRouter`. */
export async function performPush(nav: NavigateFunction, path: string, options?: PushPathOptions): Promise<void> {
  if (isChangingLanguage()) return;
  if (blocksSelfNavigation(path)) return;
  if (isPathTabDisabled(path)) return;

  const { preparePanePush } = await import("feat/layout/panes/pane-navigation");
  const paneOptions = await preparePanePush(nav, options?.paneSide ?? "leading", path, options);
  if (paneOptions === null) return;
  options = paneOptions;

  let animOption = options?.animation ?? "auto";

  if (Capacitor.isNativePlatform()) {
    await Keyboard.hide();
  }

  // Everything below reads or drives native navigation state, so it waits out a root swap
  // or a tab reflow rather than addressing components that are being torn down and rebuilt.
  // The organisation swap is the case that matters: it reflows the bar and raises the
  // required-info dialog off the same event, so the sheet this pushes would otherwise land
  // on a stack whose webviews have just been dropped.
  await whenNativeNavSettled();

  if (await surfacedExistingView(path)) return;

  // Sheet routes select their tab and replace whatever sheet is already up, so opening one
  // from inside another swaps rather than stacks. Derived from the registry.
  const sheet = await prepareSheetNavigation(path);
  const state: PushPathState | undefined = sheet?.dismiss
    ? {
        ...(options?.state ?? {}),
        nativeNavigation: { ...(options?.state?.nativeNavigation ?? {}), dismiss: sheet.dismiss },
      }
    : options?.state;
  // A sheet's visible transition is the native modal's own; a web slide under it is unseen.
  if (sheet) animOption = "none";

  if (animOption === "none") {
    nav(path, { state });
    return;
  }

  // A code-split screen that is still loading renders nothing, so the transition would
  // have no new state to slide in.
  await preloadRoute(path);

  if (animOption === "auto") {
    const destinationPaths = path.split("/").length;
    const currentPaths = window.location.pathname.split("/").length;
    animOption = destinationPaths >= currentPaths ? "right" : "left";
  }

  // queueViewTransition awaits the transition's own `finished` promise, so no delay is needed
  // before returning.
  await queueViewTransition(async () => {
    nav(path, { state });
  }, ["slide", animOption, ...(options?.trailingPaneOnly ? [TRAILING_PANE_TRANSITION] : [])]);
}
